import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge, Button as FButton, Progress, Table } from 'flowbite-react';
import _ from 'lodash';
import { Ability, Button, Icon, Loader, Tooltip } from '../../components';
import {formatPrice, formatMomentDate} from '../../locales';
import { Project, Quote } from '../../models';
import { RootState, useAppDispatch, useAppSelector } from '../../redux';
import { loadQuotes } from '../../redux';

interface ProjectQuotesProps {
    project: Project;
}

interface SortOptions {
    property?: string;
    asc: boolean;
}

interface Filters {
    status: string[];
    name?: string;
}

const condensedTdTheme = {
    "base": "group-first/body:group-first/row:first:rounded-tl-lg group-first/body:group-first/row:last:rounded-tr-lg group-last/body:group-last/row:first:rounded-bl-lg group-last/body:group-last/row:last:rounded-br-lg px-2 py-1 h-12"
};

function ProjectQuotes(props: ProjectQuotesProps) {
    const { project } = props;
    const { t }       = useTranslation();
    const navigate    = useNavigate();
    const dispatch    = useAppDispatch();
    const searchInput = useRef<HTMLInputElement>(null);

    const [sort, setSort] = useState<SortOptions>({
        property: 'slug',
        asc: false
    });
    const [filters, setFilters] = useState<Filters>({
        status: [],
        name: ''
    });

    const { quotes, loading } = useAppSelector((state: RootState) => state.quotes);

    useEffect(() => {
        const params: AnyObj = { project: project._id };
        if (filters.status.length)
            params.status = filters.status.join(',');

        dispatch(loadQuotes(params));
    }, [dispatch, project._id, filters.status]);

    const rowClassName = (quote: Quote, base?: string) => {
        return 'whitespace-nowrap text-gray-900 dark:text-white ' + (base || '');
    };

    const sortIcon = (property: string) => {
        const newSort: SortOptions = {
            property,
            asc: sort.property === property ? !sort.asc : true
        };
        const icon  = sort.property === property && !sort.asc ? 'down' : 'up';
        const color = sort.property === property ? 'gray-900' : 'gray-400';

        return (
            <div className="inline cursor-pointer w-8" onClick={() => setSort(newSort)}>
                <Icon type={icon} color={color} className="inline ml-1.5" size={5} />
            </div>
        );
    };

    const quoteProgression = (quote: Quote) => {
        const progression = Math.round(quote?.progression());

        return (
            <Tooltip content={progression + '%'} placement="top">
                <div>
                    <Progress
                        progress={progression}
                        size="sm"
                        color={progression === 100 ? 'green' : (progression > 20 ? 'cyan' : 'yellow')}
                    />
                </div>
            </Tooltip>
        );
    };

    const sortedQuotes = useMemo(() => {
        if (!quotes?.length)
            return [];

        if (!sort.property)
            return quotes;

        return quotes.filter((quote: Quote) => {
            if (!filters.name?.length)
                return true;

            return (
                quote.name.match(new RegExp(filters.name, 'gi')) ||
                quote.slug.toString().match(new RegExp(filters.name, 'gi'))
            );
        }).sort((a: Quote, b: Quote) => {
            let result = 0;
            switch (sort.property) {
                case 'slug':
                    result = a.slug > b.slug ? 1 : -1;
                    break;
                case 'name':
                    result = a.name > b.name ? 1 : -1;
                    break;
                case 'status':
                    result = a.statusIndex() > b.statusIndex() ? 1 : -1;
                    break;
                case 'price':
                    result = a.price - b.price;
                    break;
                case 'submittedAt':
                    result = (a.submittedAt || 0) > (b.submittedAt || 0) ? 1 : -1;
                    break;
            }

            return sort.asc ? result : result * -1;
        });
    }, [quotes, sort, filters.name]);

    const total = useMemo(() => {
        if (!sortedQuotes?.length)
            return 0;
        return sortedQuotes.reduce((acc: number, quote: Quote) => acc + quote.price, 0);
    }, [sortedQuotes]);

    const filterOnStatus = (status: string) => {
        let newStatus = _.clone(filters.status);
        if (newStatus.includes(status))
            newStatus = newStatus.filter((s: string) => s !== status);
        else
            newStatus.push(status);
        setFilters({ ...filters, status: newStatus });
    };

    const statusFilterBtnClassname = (i: number, max: number) => {
        let className = 'flex-1 w-40 focus:ring-0 ';
        if (i === 0)
            className += 'rounded-bl-md rounded-r-none';
        else if (i === max)
            className += 'border-t border-l-0 rounded-l-none';
        else
            className += 'border-t border-l-0 rounded-none';

        return className;
    };

    const handleKeyPress = (e: AnyObj) => {
        if ((e.key === 'Enter' || e.code === 'Enter') && sortedQuotes.length)
            return navigate(`/quotes/${sortedQuotes[0]._id}`);

        if (e.key === 'Escape' || e.code === 'Escape')
            setFilters({ ...filters, name: '' });
    };

    return (
        <div className="ProjectQuotes">
            <div className="mb-4 relative flex items-center justify-center">
                <div className="relative flex w-full max-w-md">
                    <input
                        type="text"
                        value={filters?.name}
                        onChange={e => setFilters({ ...filters, name: e.target.value })}
                        onKeyUp={handleKeyPress}
                        ref={searchInput}
                        className="w-full h-9 text-gray-600 dark:text-gray-300 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 rounded-md focus:ring-gray-300 dark:ring-gray-600 border-0 focus:border-0 py-0 pr-8"
                    />
                    <Icon type="search" color="gray-600" className="absolute top-2.5 right-2.5" />
                </div>
                <Ability can="quote:create">
                    <div className="absolute right-0 top-0">
                        <Button
                            className="w-auto"
                            title={t('quotes.add_a_quote')}
                            color="navigate"
                            to="/quotes/new"
                            icon="plus"
                        />
                    </div>
                </Ability>
            </div>

            <div className="mb-4 w-100 overflow-x-auto text-center">
                <div className="flex flex-row inline-flex">
                    {Quote.allStatus().map((status: string, i: number) => (
                        <FButton
                            key={status}
                            color={filters.status.includes(status) ? 'info' : 'gray'}
                            onClick={() => filterOnStatus(status)}
                            className={statusFilterBtnClassname(i, Quote.allStatus().length - 1)}
                        >
                            {t(`quotes.status_type.${status}`)}
                        </FButton>
                    ))}
                    {(filters.status.length > 0) && (
                        <Button
                            title=""
                            icon="close"
                            color="none"
                            iconColor="red-900"
                            className="shadow-none"
                            onClick={() => setFilters({ ...filters, status: [] })}
                        />
                    )}
                </div>
            </div>

            {loading === 'pending' ? (
                <Loader />
            ) : (
                <div className="w-full overflow-x-auto">
                    <Table striped hoverable className="w-full">
                        <Table.Head>
                            <Table.HeadCell className="w-fit p-0 text-center">#</Table.HeadCell>
                            <Table.HeadCell>{t('quotes.slug')}{sortIcon('slug')}</Table.HeadCell>
                            <Table.HeadCell>{t('quotes.name')}{sortIcon('name')}</Table.HeadCell>
                            <Table.HeadCell>{t('quotes.status')}{sortIcon('status')}</Table.HeadCell>
                            <Table.HeadCell className="text-center">{t('quotes.last_action')}{sortIcon('submittedAt')}</Table.HeadCell>
                            <Table.HeadCell className="text-right">{t('quotes.price')}{sortIcon('price')}</Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y">
                            {sortedQuotes?.map((quote: Quote, i: number) => (
                                <Table.Row
                                    key={quote?._id}
                                    onClick={() => navigate(`/quotes/${quote._id}`)}
                                    className="dark:border-gray-700 dark:bg-gray-800 cursor-pointer"
                                >
                                    <Table.Cell theme={condensedTdTheme} className={rowClassName(quote, 'font-light text-gray-600 dark:text-gray-300 p-0 text-center')}>
                                        {i + 1}
                                    </Table.Cell>
                                    <Table.Cell theme={condensedTdTheme} className={rowClassName(quote, 'font-bold')}>
                                        {quote?.slug}
                                    </Table.Cell>
                                    <Table.Cell theme={condensedTdTheme} className={rowClassName(quote, 'font-light')}>
                                        <div>{quote?.name}</div>
                                        {quoteProgression(quote)}
                                        {quote?.client && (
                                            <small className="font-light">{quote.client.name()}</small>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell theme={condensedTdTheme} className={rowClassName(quote, 'font-light')}>
                                        <div className="flex flex-row justify-center items-center">
                                            <Badge color={quote.statusColor()} className="w-fit">
                                                {t(`quotes.status_type.${quote?.status}`)}
                                            </Badge>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell theme={condensedTdTheme} className={rowClassName(quote, 'font-light text-center')}>
                                        <Icon type="date" className="inline" />&nbsp;
                                        {quote.lastAction().date
                                            ? formatMomentDate(quote.lastAction().date, 'L')
                                            : undefined}
                                    </Table.Cell>
                                    <Table.Cell theme={condensedTdTheme} className={rowClassName(quote, 'font-bold text-right')}>
                                        {quote.formattedPrice()}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                            <Table.Row key="-1" className="dark:border-gray-700 dark:bg-gray-800">
                                <Table.Cell></Table.Cell>
                                <Table.Cell></Table.Cell>
                                <Table.Cell></Table.Cell>
                                <Table.Cell></Table.Cell>
                                <Table.Cell>
                                    <h3 className="text-right text-xl font-bold text-gray-900 dark:text-gray-100">Total</h3>
                                </Table.Cell>
                                <Table.Cell>
                                    <h3 className="text-right text-xl font-bold text-gray-900 dark:text-gray-100">
                                        {formatPrice(total)}
                                    </h3>
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </div>
            )}
        </div>
    );
}

export default ProjectQuotes;
